<script lang="ts">
    /* eslint-disable vue/multi-word-component-names */
    export default {
        name: "Dagsoversikt",
        tabName: "Dagsöversikt",
        refreshTab: true,
    };
</script>

<script setup lang="ts">
    import { ref, computed, onMounted, onBeforeUnmount } from "vue";
    import Button from "@/components/Button.vue";
    import moment from "moment";
    import DagsoversiktList from "./DagsoversiktList.vue";
    import { chain } from "lodash";
    import { printInIframe } from "@/utils/printWindow";
    import ModalBasic, { BasicModalVariant } from "@/components/generic/ModalBasic.vue";
    import KFilter from "@/components/KFilter/KFilter.vue";
    import { generateSchema } from "@/components/forms/Form/schemaHelpers";
    import { getConfig, klinikenApi, getErrorMessage } from "@/api";
    import { openDialog } from "@/utils";
    import { useStore } from "@/store";
    import emitter from "tiny-emitter/instance";
    import qs from "qs";

    const props = defineProps<{
        uid?: number;
        tabId: number;
        parameters: Record<string, any>;
    }>();

    const store = useStore();

    const can = computed(() => store.getters["ability/can"]);

    const schema = generateSchema({
        addToAll: { ignoreEmptyData: true },
        schema: [
            {
                component: "KText",
                type: "date",
                name: "datum",
                label: "Datum",
                ignoreEmptyData: false,
                nonClearableTag: true,
            },
            {
                component: "ProfileAutocomplete",
                name: "current_huvudoperator",
                label: "Operatör",
                urlParams: { roll__code: "kirurg" },
                allowSearch: true,
                autoPosition: true,
            },
            {
                component: "VardformAutocomplete",
                label: "Vårdform",
                autoPosition: true,
            },
            {
                component: "KAutocomplete",
                name: "current_sal",
                label: "Sal",
                url: "/operation/salar/",
                optionMapper: ({ id, namn }: { id: number; namn: string }) => ({ value: id, label: namn }),
                autoPosition: true,
                multiple: true,
            },
        ],
    });

    interface FilterData {
        datum: string;
        current_sal: any[] | null;
        current_huvudoperator: string | null;
        planerad_vardform: string | null;
    }

    const anmalningar = ref<any[]>([]);
    const filterData = ref<FilterData>({
        datum: moment().format("YYYY-MM-DD"),
        current_sal: null,
        current_huvudoperator: null,
        planerad_vardform: null,
    });
    const dagskommentar = ref<string>("");
    const printButtonDisabled = ref<boolean>(false);

    const printablecontent = ref<HTMLElement | null>(null);
    const dialog = ref<InstanceType<typeof ModalBasic> | null>(null);

    const currentDate = computed(() => moment().format("YYYY-MM-DD"));
    const tomorrowDate = computed(() => moment().add(1, "days").format("YYYY-MM-DD"));

    const groupedAnmalningar = computed(() =>
        chain(anmalningar.value)
            .groupBy((anmalan: any) => anmalan.current_sal_display?.namn ?? "Ingen sal bokad")
            .map((anmalningarGroup: any[], sal: string) => ({ anmalningar: anmalningarGroup, sal }))
            .value()
    );

    const isSelectedToday = computed(() => filterData.value.datum === currentDate.value);
    const isSelectedTomorrow = computed(() => filterData.value.datum === tomorrowDate.value);
    const hasAnmalningar = computed(() => anmalningar.value.length > 0);

    async function onFilterSubmit({ formData }: { formData: any }) {
        printButtonDisabled.value = false;
        filterData.value = {
            ...formData,
            datum: formData.datum || filterData.value.datum,
        };
        await fetchDagsinformation(filterData.value.datum);
        await fetchAnmalningar();
    }

    async function fetchAnmalningar() {
        await store.dispatch("tabs/displaySpinner", { id: props.tabId, display: true });

        const currentSal = filterData.value.current_sal;

        try {
            const params = {
                bokad_operation_datum: filterData.value.datum,
                current_sal__id: Array.isArray(currentSal) && currentSal.length ? currentSal.join(",") : undefined,
                current_huvudoperator: filterData.value.current_huvudoperator,
                planerad_vardform: filterData.value.planerad_vardform,
                ordering: "current_sal__ordning,current_operation_tid",
            };

            const response = await klinikenApi.get(
                `/operation/oversikt/?${qs.stringify(params, { indices: false, skipNulls: true })}`,
                getConfig({}, "Dagsöversikt")
            );
            anmalningar.value = response?.data?.results ?? response.data;

            if (!hasAnmalningar.value) {
                printButtonDisabled.value = true;
            }
        } catch (error) {
            openDialog(getErrorMessage(error), "error");
        } finally {
            await store.dispatch("tabs/displaySpinner", { id: props.tabId, display: false });
        }
    }

    async function print() {
        if (
            !can.value({
                action: "read",
                subject: "dagsoversikt",
            })
        ) {
            return;
        }
        if (!printablecontent.value) return;

        try {
            await printInIframe({
                url: "/operation/anmalan/utskrift/",
                items: anmalningar.value,
                logName: "Dagsöversikt",
                contentElement: printablecontent.value,
                orientation: "landscape",
                title: `Dagsöversikt ${filterData.value.datum}`,
            });
        } catch (_) {
            dialog.value?.show({
                variant: BasicModalVariant.ALERT,
                title: "Det gick inte att få ett utskrift-ID.",
                okButton: "OK",
            });
        }
    }

    async function fetchDagsinformation(date: string) {
        try {
            const response = await klinikenApi.get(`/operation/dagsinformation/${date}/`, getConfig());
            dagskommentar.value = response.data?.kommentar || "";
        } catch {
            dagskommentar.value = "";
        }
    }

    onMounted(async () => {
        if (props.parameters?.date) {
            filterData.value.datum = props.parameters.date;
        }
        await fetchDagsinformation(filterData.value.datum);
        await fetchAnmalningar();

        emitter.on(`refreshTab-${props.tabId}`, async () => {
            await fetchAnmalningar();
        });
    });

    onBeforeUnmount(() => {
        emitter.off(`refreshTab-${props.tabId}`);
    });
</script>

<template>
    <div class="dagsoversikt">
        <div class="header">
            <h1>Dagsöversikt</h1>
        </div>

        <KFilter
            name="dagsoversikt"
            :schema="schema"
            :defaultValue="filterData"
            @submit="onFilterSubmit"
            :expanded="true"
        >
            <template #quick-actions="{ callback }">
                <Button
                    type="button"
                    size="secondary"
                    variant="selectable"
                    :class="{ active: isSelectedToday }"
                    @click="callback({ values: { datum: currentDate } })"
                >
                    Idag
                </Button>
                <Button
                    type="button"
                    size="secondary"
                    variant="selectable"
                    :class="{ active: isSelectedTomorrow }"
                    @click="callback({ values: { datum: tomorrowDate } })"
                >
                    Imorgon
                </Button>
            </template>
        </KFilter>

        <Button class="print" size="secondary" @click="print" :disabled="printButtonDisabled">Skriv ut</Button>

        <div class="count">Visar {{ anmalningar.length }} operationer</div>

        <hr />

        <div ref="printablecontent">
            <div class="table-heading-caption-kommentar">
                <div class="table-caption">Dagsöversikt operation</div>
                <h4 v-if="dagskommentar.length" class="dagskommentar">
                    Kommentar: <strong>{{ dagskommentar }}</strong>
                </h4>
            </div>
            <DagsoversiktList
                v-for="(group, index) in groupedAnmalningar"
                :key="index"
                :group="group"
                :groupIndex="index"
            />
        </div>
        <ModalBasic ref="dialog" />
    </div>
</template>

<style lang="scss" scoped>
    @import "@/style/variables";
    @import "@/style/container";

    .dagsoversikt {
        height: 100%;

        .header {
            display: flex;
            align-items: center;
            gap: 20px;
            margin-bottom: 26px;

            h1 {
                margin: 0;
            }
        }

        .button.print {
            margin-top: 20px;
        }

        .count {
            font-style: normal;
            font-weight: 400;
            font-size: 22px;
            line-height: 24px;
            margin: 25px 0 15px;
        }

        hr {
            border: 1px solid $list-border-color;
        }

        .table-heading-caption-kommentar {
            display: flex;
            column-gap: 14px;
        }

        .table-caption {
            font-style: normal;
            font-weight: 400;
            font-size: $font-size-small;
            line-height: 28px;
            color: $color-active-dark;
            font-family:
                Roboto Medium,
                sans-serif;
            white-space: nowrap;
        }

        h4.dagskommentar {
            margin: 0;
            padding: 0;
            font-size: $font-size-small;
        }

        strong {
            margin: 0;
            padding: 0;
            font-size: $font-size-small;
        }
    }
</style>
